/**
 * Unit conversion utilities for the application
 * All internal calculations use metric system (cm, kg, mm)
 * Display conversions are handled here
 */

export type Unit = 'cm' | 'inches';
export type WeightUnit = 'kg' | 'lbs';

// Conversion constants
const INCHES_TO_CM = 2.54;
const CM_TO_INCHES = 1 / INCHES_TO_CM;
const LBS_TO_KG = 0.453592;
const KG_TO_LBS = 1 / LBS_TO_KG;

/**
 * Convert height from inches to centimeters
 */
export const inchesToCm = (inches: number): number => {
  return inches * INCHES_TO_CM;
};

/**
 * Convert height from centimeters to inches
 */
export const cmToInches = (cm: number): number => {
  return cm * CM_TO_INCHES;
};

/**
 * Convert weight from pounds to kilograms
 */
export const lbsToKg = (lbs: number): number => {
  return lbs * LBS_TO_KG;
};

/**
 * Convert weight from kilograms to pounds
 */
export const kgToLbs = (kg: number): number => {
  return kg * KG_TO_LBS;
};

/**
 * Convert height to display value based on unit preference
 */
export const convertHeightForDisplay = (heightCm: number, displayUnit: Unit): number => {
  if (displayUnit === 'inches') {
    return Math.round(cmToInches(heightCm) * 10) / 10; // Round to 1 decimal place
  }
  return Math.round(heightCm); // Round to whole cm
};

/**
 * Convert weight to display value based on unit preference
 */
export const convertWeightForDisplay = (weightKg: number, displayUnit: Unit): number => {
  if (displayUnit === 'inches') {
    return Math.round(kgToLbs(weightKg) * 10) / 10; // Round to 1 decimal place
  }
  return Math.round(weightKg * 10) / 10; // Round to 1 decimal place for kg
};

/**
 * Convert height from input value to metric (cm) for storage
 */
export const convertHeightToMetric = (value: number, inputUnit: Unit): number => {
  if (inputUnit === 'inches') {
    return inchesToCm(value);
  }
  return value;
};

/**
 * Convert weight from input value to metric (kg) for storage
 */
export const convertWeightToMetric = (value: number, inputUnit: Unit): number => {
  if (inputUnit === 'inches') {
    return lbsToKg(value);
  }
  return value;
};

/**
 * Get the appropriate unit label for height display
 */
export const getHeightUnitLabel = (unit: Unit): string => {
  return unit === 'inches' ? 'in' : 'cm';
};

/**
 * Get the appropriate unit label for weight display
 */
export const getWeightUnitLabel = (unit: Unit): string => {
  return unit === 'inches' ? 'lbs' : 'kg';
};

/**
 * Format height value with unit label
 */
export const formatHeight = (heightCm: number, displayUnit: Unit): string => {
  const value = convertHeightForDisplay(heightCm, displayUnit);
  const unit = getHeightUnitLabel(displayUnit);
  return `${value} ${unit}`;
};

/**
 * Format weight value with unit label
 */
export const formatWeight = (weightKg: number, displayUnit: Unit): string => {
  const value = convertWeightForDisplay(weightKg, displayUnit);
  const unit = getWeightUnitLabel(displayUnit);
  return `${value} ${unit}`;
};
